import { toJSTimestamp } from '@util/helper';
import { useAPI } from 'src/hooks';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import loadable from '@loadable/component';
import styled from '@emotion/styled';

const EventBlock = loadable(() => import('./EventBlock'));
const LoadingContainer = loadable(() => import('@com/utils/LoadingContainer'));
const SkeletonImage = loadable(() => import('@com/utils/SkeletonImage'));

interface Props {
  chainId: number;
  collectionAddress: string;
  tokenId: number;
  isOwned: boolean;
}

const EventDiv = styled.div`
  overflow-y: scroll;
`;

function NftEventList(props: Props): JSX.Element {
  /* Define state / hook */
  const { collectionAddress, tokenId, chainId, isOwned } = props;
  const { getNftEvents, getNftQRCode } = useAPI();
  const { data, isSuccess, isError } = useQuery(
    [getNftEvents.name, chainId, collectionAddress, tokenId],
    () => {
      return getNftEvents(chainId, collectionAddress, tokenId);
    }
  );
  const { data: qrCodeURL, refetch } = useQuery(
    [getNftQRCode.name, chainId, collectionAddress, tokenId],
    () => {
      return getNftQRCode(chainId, collectionAddress, tokenId);
    },
    {
      enabled: isOwned,
      refetchInterval: 15 * 60 * 1000,
    }
  );

  /* Custom Data Object for render or other usages */

  /* Render control function / logic */

  /* Data Change Listeners - useEffect/useMemo/useCallback */
  useEffect(() => {
    const handleActivityTrue = async () => {
      let urlParams: URLSearchParams;
      if (qrCodeURL) {
        urlParams = new URLSearchParams(qrCodeURL);
        const qrCodeData = urlParams.get('chl');
        if (qrCodeData) {
          urlParams = new URLSearchParams(qrCodeData);
          const ttl = toJSTimestamp(urlParams.get('ttl'));
          if (Date.now() >= ttl) refetch();
        }
      }
    };

    window.addEventListener('focus', handleActivityTrue);
    return () => {
      window.removeEventListener('focus', handleActivityTrue);
    };
  }, [qrCodeURL]);

  /* Styling */

  /* Render */
  return (
    <LoadingContainer
      iconClassName="w-100 d-flex justify-content-center align-items-center mt-5"
      isError={isError}
      isSuccess={isSuccess}
    >
      <div className="row">
        {isOwned ? (
          <>
            <div className="col-lg-4 col-sm-12 mb-5">
              <SkeletonImage ratio={1} src={qrCodeURL} />
            </div>
            <EventDiv className="col-lg-8 col-sm-12">
              {data?.map((item) => (
                <EventBlock key={item.id} event={item} />
              ))}
            </EventDiv>
          </>
        ) : (
          <EventDiv className="col-12">
            {data?.map((item) => (
              <EventBlock key={item.id} event={item} />
            ))}
          </EventDiv>
        )}
      </div>
    </LoadingContainer>
  );
}

export default NftEventList;
